import { useCallback } from 'react'
import { isSoftwareVersionSupported } from '../utils'
import { useSoftwareVersion } from './use-software-version'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'

const supportedSoftwareVersionYear = '2024'
const supportedSoftwareVersionMonthPatch = '04'

interface SnackbarMessageOptions {
  customSuccessMessageKey?: string
  pageName?: string
  accordionName?: string
  showWarningSnackbar?: boolean
  isEmsSetting?: boolean
}

type UseSnackbarMessageBasedOnSoftwareVersion = {
  handleDisplaySnackbarMessageBasedOnSoftwareVersion: (options: SnackbarMessageOptions) => void
}

/**
 * This hook returns the success message display handler based on the software version.
 * in the software version before 2024.04, the success message shows that the user has to wait up to 15min.
 * in the newer software version, he doesn't have to wait.
 */
export const useSnackbarMessageBasedOnSoftwareVersion = (): UseSnackbarMessageBasedOnSoftwareVersion => {
  const { t } = useCustomTranslation()
  const { softwareVersion } = useSoftwareVersion()
  const { enqueueSuccessStack, enqueueWarningStack } = useSnackbar()

  const doesSupportNewestVersion = isSoftwareVersionSupported(
    `${supportedSoftwareVersionYear}.${supportedSoftwareVersionMonthPatch}`,
    softwareVersion ?? ''
  )
  const oldSoftwareVersionWarningMessageKey = 'common.systemSettings.api.warning.warning-form-submission'
  const emsWarningMessageKey = 'common.systemSettings.api.warning.warning-form-submission-ems-settings'
  const oldSoftwareVersionSuccessMessageKey = 'common.systemSettings.api.success.success-form-submission'
  const emsSettingsSuccessMessageKey = 'common.systemSettings.api.success.success-form-submission-ems-settings'
  const newSoftwareVersionSuccessMessageKey = 'common.systemSettings.api.success.success-form-submission-without-waiting'

  const handleDisplaySnackbarMessageBasedOnSoftwareVersion = useCallback(
    (options: SnackbarMessageOptions) => {
      const { customSuccessMessageKey, showWarningSnackbar, accordionName, pageName, isEmsSetting = false } = options
      let successMessageKey: string

      const warningMessageKey = !doesSupportNewestVersion && showWarningSnackbar ? oldSoftwareVersionWarningMessageKey : emsWarningMessageKey
      const showWarningMessage =
        (doesSupportNewestVersion && isEmsSetting && showWarningSnackbar) || (!doesSupportNewestVersion && showWarningSnackbar)

      if (!doesSupportNewestVersion) {
        successMessageKey = customSuccessMessageKey ?? oldSoftwareVersionSuccessMessageKey
      } else if (isEmsSetting) {
        successMessageKey = emsSettingsSuccessMessageKey
      } else {
        successMessageKey = newSoftwareVersionSuccessMessageKey
      }

      enqueueSuccessStack(t(successMessageKey, { pageName, accordionName }))

      if (showWarningMessage) {
        enqueueWarningStack(t(warningMessageKey, { pageName, accordionName }))
      }
    },
    [enqueueSuccessStack, enqueueWarningStack, doesSupportNewestVersion, t]
  )

  return { handleDisplaySnackbarMessageBasedOnSoftwareVersion }
}
